import React, {Fragment, useEffect, useState} from 'react';
import {Modal, Button, Input, Checkbox, Loader} from 'semantic-ui-react';
import {apiPost} from '../../../services/apiClient';
import {toast} from 'react-toastify';
import ConfirmModal from '../../../modals/confirmModal';
import Info from '../../../components/Info';
import './parcelCustomersManageModal.scss';

const ParcelCustomersManageModal = ({open, companyId, onClose, onChanged}) => {
    const [name, setName] = useState('');
    const [filter, setFilter] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const confirmModalRef = React.useRef(null);

    const loadCustomers = () => {
        if (!companyId) return Promise.resolve();
        setLoading(true);
        return apiPost('ref/parcelcustomers/list', {companyId})
            .then(data => setCustomers((data && data.entries) || []))
            .catch(err => toast.error((err && (err.error || err.Error)) || 'Не удалось загрузить список'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open && companyId) {
            setName('');
            setFilter('');
            setSelectedIds([]);
            loadCustomers();
        }
    }, [open, companyId]);

    const getFilteredCustomers = () => {
        const query = (filter || '').trim().toLowerCase();
        if (!query) {
            return customers;
        }
        return customers.filter(c => (c.name || '').toLowerCase().indexOf(query) >= 0);
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.concat(id));
    };

    const addCustomer = () => {
        if (!name.trim()) return;
        setLoading(true);
        apiPost('ref/parcelcustomers', {companyId, name: name.trim()})
            .then(() => {
                setName('');
                toast.success('Заказчик добавлен');
                return loadCustomers();
            })
            .then(() => onChanged && onChanged())
            .catch(err => toast.error((err && (err.error || err.Error)) || 'Не удалось добавить'))
            .finally(() => setLoading(false));
    };

    const deleteSelected = () => {
        if (!selectedIds.length || !confirmModalRef.current) return;
        confirmModalRef.current.open('Удалить запись(и)?', 'Выбранные заказчики будут удалены из списка выбора.')
            .then(() => {
                setLoading(true);
                return apiPost('ref/parcelcustomers/delete', {companyId, ids: selectedIds});
            })
            .then(() => {
                setSelectedIds([]);
                toast.success('Записи удалены');
                return loadCustomers();
            })
            .then(() => onChanged && onChanged())
            .catch(err => {
                if (err) {
                    toast.error((err && (err.error || err.Error)) || 'Не удалось удалить');
                }
            })
            .finally(() => setLoading(false));
    };

    const renderList = () => {
        const filtered = getFilteredCustomers();
        if (loading && !customers.length) {
            return <div className="parcel-customers-manage-modal__loading"><Loader active inline size="small" /> Загрузка...</div>;
        }
        if (!customers.length) {
            return <div className="parcel-customers-manage-modal__empty">Нет записей. Добавьте заказчика выше.</div>;
        }
        if (!filtered.length) {
            return <div className="parcel-customers-manage-modal__empty">Ничего не найдено по фильтру.</div>;
        }
        return filtered.map(c => {
            const selected = selectedIds.includes(c.id);
            return (
                <div
                    key={c.id}
                    className={'parcel-customers-manage-modal__item' + (selected ? ' parcel-customers-manage-modal__item--selected' : '')}
                >
                    <Checkbox
                        label={c.name}
                        checked={selected}
                        onChange={() => toggleSelect(c.id)}
                    />
                </div>
            );
        });
    };

    return (
        <Fragment>
            <Modal open={open} onClose={onClose} size="small" className="parcel-customers-manage-modal">
                <Modal.Header>Добавить/удалить заказчика</Modal.Header>
                <Modal.Content>
                    <div className="parcel-customers-manage-modal__notice">
                        <Info text="Добавить или удалить заказчика из списка выбора. Уже оформленные посылки не изменятся." />
                        <span>Справочник заказчиков для вашей организации</span>
                    </div>

                    <div className="parcel-customers-manage-modal__section">
                        <div className="parcel-customers-manage-modal__section-title">Новый заказчик</div>
                        <div className="parcel-customers-manage-modal__label-row">
                            <span>Наименование</span>
                            <Info text="ФИО или название компании" />
                        </div>
                        <Input
                            fluid
                            value={name}
                            onChange={(e, {value}) => setName(value)}
                            placeholder="Введите наименование"
                            disabled={loading}
                        />
                    </div>

                    <div className="parcel-customers-manage-modal__section parcel-customers-manage-modal__section--list">
                        <div className="parcel-customers-manage-modal__section-title">
                            Список заказчиков
                            {customers.length > 0 && (
                                <span style={{fontWeight: 400, color: '#6b7c93', marginLeft: 8}}>
                                    ({getFilteredCustomers().length}{filter.trim() ? ' из ' + customers.length : ''})
                                </span>
                            )}
                        </div>
                        {customers.length > 0 && (
                            <Input
                                fluid
                                icon="search"
                                iconPosition="left"
                                value={filter}
                                onChange={(e, {value}) => setFilter(value)}
                                placeholder="Фильтр по наименованию"
                                disabled={loading}
                                className="parcel-customers-manage-modal__filter"
                            />
                        )}
                        <div className="parcel-customers-manage-modal__list">
                            {renderList()}
                        </div>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <div className="parcel-customers-manage-modal__actions-row">
                        <Button onClick={onClose} disabled={loading}>Закрыть</Button>
                        <Button
                            negative
                            disabled={!selectedIds.length || loading}
                            onClick={deleteSelected}
                        >
                            Удалить{selectedIds.length > 0 ? ' (' + selectedIds.length + ')' : ''}
                        </Button>
                        <Button primary disabled={!name.trim() || loading} loading={loading} onClick={addCustomer}>
                            Добавить
                        </Button>
                    </div>
                </Modal.Actions>
            </Modal>
            <ConfirmModal ref={confirmModalRef} />
        </Fragment>
    );
};

export default ParcelCustomersManageModal;



// WEBPACK FOOTER //
// ./App/pages/parcels/form_v3/parcelCustomersManageModal.jsx